import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";

export const LandingTarget = () => {
  const targetAudience = [
    "Você se sente sobrecarregado(a) por pensamentos ansiosos ou preocupações constantes?",
    "Você busca um espaço seguro e sem julgamentos para desabafar sobre seu dia, seus sentimentos e desafios?",
    "Você gostaria de entender melhor por que reage de certas maneiras e como seus pensamentos afetam suas emoções?",
    "Você tem interesse em aprender ferramentas práticas para seu bem-estar diário?",
    "Você precisa, às vezes, de uma ajuda rápida para acalmar a mente em um momento de estresse?",
    'Você procura mais clareza e menos "ruído mental" para focar no que realmente importa?',
  ];

  return (
    <section className="relative w-full min-h-[700px] md:min-h-[864px] py-12 md:py-16 lg:py-24 px-6 md:px-12 overflow-hidden">
      {/* Aqueous Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#D8DEE9]/25 via-white to-[#D8DEE9]/20 -z-10"></div>

      {/* Animated Blobs */}
      <div
        className="absolute top-0 left-0 w-[700px] h-[700px] rounded-full opacity-8 blur-3xl -z-10"
        style={{
          background: "radial-gradient(circle, rgba(74, 78, 105, 0.3) 0%, rgba(74, 78, 105, 0) 70%)",
          animation: "drift 25s ease-in-out infinite",
        }}
      ></div>
      <div
        className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full opacity-12 blur-3xl -z-10"
        style={{
          background: "radial-gradient(circle, rgba(255, 209, 102, 0.5) 0%, rgba(255, 209, 102, 0) 70%)",
          animation: "drift 30s ease-in-out infinite reverse",
        }}
      ></div>

      {/* Header */}
      <div
        className="text-center mb-12"
        style={{
          animation: "fadeInUp 0.8s ease-out",
        }}
      >
        <h2 className="text-3xl md:text-4xl font-bold text-[#4A4E69] mb-4 mt-2">KopfAI é para você?</h2>
        <p className="text-base md:text-lg text-[#272D37]/70 max-w-3xl mx-auto">
          Nosso objetivo é ajudar qualquer pessoa que busque mais clareza mental.
          <br className="hidden md:inline" />
          Você se identifica com algum destes pontos?
        </p>
      </div>

      {/* Checklist */}
      <div className="max-w-4xl mx-auto space-y-4 md:space-y-6 mb-4 md:mb-6">
        {targetAudience.map((item, index) => (
          <div
            key={index}
            className="group flex items-start gap-4 p-4 md:p-5 rounded-2xl bg-white/50 backdrop-blur-sm border border-[#D8DEE9]/40 transition-all duration-500 hover:bg-white/70 hover:border-[#FFD166]/50 hover:shadow-lg hover:-translate-x-2"
            style={{
              animation: `fadeInLeft 0.6s ease-out ${0.1 + index * 0.08}s backwards`,
            }}
          >
            {/* Check icon with glow */}
            <div className="relative">
              <div
                className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-lg"
                style={{
                  background: "radial-gradient(circle, rgba(255, 209, 102, 0.6), transparent 70%)",
                  transform: "scale(2)",
                }}
              ></div>
              <div className="relative w-8 h-8 md:w-9 md:h-9 rounded-full bg-gradient-to-br from-[#FFD166] to-[#FFD166]/80 flex items-center justify-center shadow-md transition-all duration-500 group-hover:scale-110 group-hover:rotate-12">
                <Check
                  className="w-5 h-5 md:w-6 md:h-6 text-[#4A4E69] transition-transform duration-500 group-hover:scale-110"
                  strokeWidth={3.5}
                />
              </div>
            </div>
            <p className="text-base sm:text-lg md:text-xl text-[#272D37]/85 leading-relaxed flex-1">{item}</p>
          </div>
        ))}
      </div>

      {/* Bottom Section */}
      <div
        className="max-w-4xl mx-auto"
        style={{
          animation: "fadeInUp 0.8s ease-out 0.6s backwards",
        }}
      >
        <div className="relative rounded-3xl bg-white/60 backdrop-blur-xl border border-[#D8DEE9]/50 p-8 md:p-10 shadow-xl mb-8 overflow-hidden group transition-all duration-500 hover:shadow-2xl">
          {/* Decorative blobs */}
          <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-[#FFD166]/10 blur-3xl -z-10"></div>
          <div className="absolute bottom-0 left-0 w-40 h-40 rounded-full bg-[#4A4E69]/5 blur-3xl -z-10"></div>

          <div className="relative z-10">
            <h3 className="text-xl font-bold text-[#4A4E69] text-center mb-4">
              Se você se identificou com algum desses pontos, a KopfAI pode te ajudar.
            </h3>
            <p className="text-base md:text-lg text-[#272D37]/80 text-center leading-relaxed">
              A KopfAI é estruturada com diálogos reflexivos e respostas emocionalmente inteligentes. Ela orienta os
              usuários com técnicas baseadas em TCC (Terapia Cognitivo-Comportamental), exercícios de mindfulness,
              prompts de diário, ativação comportamental, escrita guiada, meditação e outras ferramentas com base
              clínica, conforme o que a pessoa está sentindo.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div
          className="flex flex-col items-center"
          style={{
            animation: "fadeInUp 0.8s ease-out 0.8s backwards",
          }}
        >
          <Link to="/auth?tab=signup">
            <Button
              size="lg"
              className="h-14 px-12 rounded-full bg-[#FFD166] hover:bg-[#FFD166]/90 text-[#4A4E69] text-lg font-bold mb-4 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 group"
              style={{
                boxShadow: "0 15px 40px rgba(255, 209, 102, 0.3)",
              }}
            >
              <span className="transition-transform duration-300 group-hover:scale-105">Começar agora</span>
            </Button>
          </Link>
          <p className="text-sm text-[#272D37]/50 flex items-center gap-2">
            <span className="inline-block w-1 h-1 rounded-full bg-[#FFD166]"></span>
            Gratuito para começar • Sem compromisso
          </p>
        </div>
      </div>

      <style>{`
        @keyframes drift {
          0%, 100% { 
            transform: translate(0, 0) scale(1); 
          }
          50% { 
            transform: translate(50px, 50px) scale(1.15); 
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeInLeft {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </section>
  );
};
